import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  MoreVertical,
  Newspaper,
  Plus,
  Sparkles,
  TreePine,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { TagInput } from "@/components/admin/TagInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateSeedEvent,
  useDeleteSeedEvent,
  useGenerateFromSeedEvent,
  useGenerationJob,
  useSeedEventSources,
  useSeedEvents,
  useUpdateSeedEvent,
} from "@/hooks/admin/seedEvents";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { SeedEvent, SeedEventStatus, SeedEventType, Source } from "@/types";

// ── Controlled vocabularies ───────────────────────────────────────────────────

const GEOGRAPHY_VALUES = [
  "UK",
  "US",
  "Europe",
  "Emerging Markets",
  "China",
  "Global",
  "Region-specific",
];

const ASSET_CLASS_VALUES = [
  "Equities",
  "FX",
  "Rates",
  "Credit",
  "Commodities",
  "Real Estate",
  "Digital Assets",
  "Multi-asset",
  "N/A",
];

const STATUS_LABELS: Record<SeedEventStatus, string> = {
  qualified: "Qualified",
  pending: "Pending",
  evaluating: "Evaluating",
  rejected: "Rejected",
  duplicate: "Duplicate",
};

const STATUS_TONES: Record<SeedEventStatus, BadgeTone> = {
  pending: "neutral",
  evaluating: "info",
  qualified: "success",
  rejected: "danger",
  duplicate: "warning",
};

// Radix Select forbids an empty-string item value, so use a sentinel for "all".
const ALL = "__all__";

const PAGE_SIZE = 25;

// ── Multi-select chips ────────────────────────────────────────────────────────

function MultiSelect({
  options,
  values,
  onChange,
}: {
  options: string[];
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (opt: string) =>
    values.includes(opt) ? onChange(values.filter((v) => v !== opt)) : onChange([...values, opt]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${values.includes(opt) ? "bg-primary text-white hover:bg-primary/90" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// ── Event type badge ──────────────────────────────────────────────────────────

function EventTypeBadge({ type }: { type: SeedEventType }) {
  return type === "news" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
      <Newspaper className="h-3 w-3" />
      News
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
      <TreePine className="h-3 w-3" />
      Evergreen
    </span>
  );
}

// ── Generate modal ────────────────────────────────────────────────────────────

function GenerateModal({
  event,
  onClose,
  onStarted,
}: {
  event: SeedEvent;
  onClose: () => void;
  onStarted: (eventId: string, count: number) => void;
}) {
  const [count, setCount] = useState(1);
  const displaySource = event.source?.name || event.source_name || event.source_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <h2 className="font-semibold text-lg">Generate Topics</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {/* Seed event details */}
          <div className="space-y-2 rounded-xl bg-gray-50 p-4">
            <div className="flex items-start gap-2">
              <EventTypeBadge type={event.event_type} />
              {event.event_date && (
                <span className="mt-0.5 text-gray-500 text-xs">
                  {new Date(event.event_date).toLocaleDateString()}
                </span>
              )}
            </div>
            <p className="font-semibold text-gray-900 leading-snug">{event.title}</p>
            {event.summary && (
              <p className="text-gray-600 text-sm leading-relaxed">{event.summary}</p>
            )}
            {displaySource && <p className="text-gray-400 text-xs">Source: {displaySource}</p>}
          </div>

          {/* Count picker */}
          <div>
            <label className="mb-2 block font-medium text-gray-700 text-sm">
              Topics to generate
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCount(n)}
                  className={`flex-1 rounded-lg border py-2 font-semibold text-sm transition-colors ${
                    count === n
                      ? "border-purple-600 bg-purple-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:border-purple-400 hover:text-purple-600"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onStarted(event.id, count);
              onClose();
            }}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2 font-medium text-white transition-colors hover:bg-purple-700"
          >
            <Sparkles className="h-4 w-4" />
            Generate {count} {count === 1 ? "Topic" : "Topics"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal (create + edit) ─────────────────────────────────────────────────────

const seedEventFormSchema = z
  .object({
    title: z.string().trim().min(1, "Title is required"),
    summary: z.string().trim().min(1, "Summary is required"),
    sourceMode: z.enum(["registry", "manual"]),
    sourceId: z.string(),
    sourceUrl: z.string(),
    sourceName: z.string(),
    eventDate: z.string(),
    eventType: z.enum(["news", "evergreen"]),
    tickers: z.array(z.string()),
    entities: z.array(z.string()),
    geography: z.array(z.string()),
    assetClass: z.array(z.string()),
    cooldownDays: z.string(),
  })
  .refine(
    (v) => {
      if (v.eventType !== "news") return true;
      return v.sourceMode === "registry" ? !!v.sourceId : !!(v.sourceUrl || v.sourceName);
    },
    { message: "News events require a source.", path: ["sourceMode"] }
  );

type SeedEventFormValues = z.infer<typeof seedEventFormSchema>;

const EMPTY_FORM: SeedEventFormValues = {
  title: "",
  summary: "",
  sourceMode: "registry",
  sourceId: "",
  sourceUrl: "",
  sourceName: "",
  eventDate: "",
  eventType: "news",
  tickers: [],
  entities: [],
  geography: [],
  assetClass: [],
  cooldownDays: "",
};

function fromEvent(event: SeedEvent): SeedEventFormValues {
  return {
    title: event.title,
    summary: event.summary ?? "",
    sourceMode: event.source_id ? "registry" : "manual",
    sourceId: event.source_id ?? "",
    sourceUrl: event.source_url ?? "",
    sourceName: event.source_name ?? "",
    eventDate: event.event_date ? event.event_date.slice(0, 10) : "",
    eventType: event.event_type,
    tickers: event.tickers ?? [],
    entities: event.entities ?? [],
    geography: event.geography ?? [],
    assetClass: event.asset_class ?? [],
    cooldownDays: event.cooldown_days != null ? String(event.cooldown_days) : "",
  };
}

function SeedEventModal({
  event,
  sources,
  onClose,
}: {
  event?: SeedEvent;
  sources: Source[];
  onClose: () => void;
}) {
  const isEditing = !!event;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createSeedEvent = useCreateSeedEvent();
  const updateSeedEvent = useUpdateSeedEvent();
  const { mutateAsync: createSeedEventAsync } = createSeedEvent;
  const { mutateAsync: updateSeedEventAsync } = updateSeedEvent;
  const form = useForm<SeedEventFormValues>({
    resolver: zodResolver(seedEventFormSchema),
    defaultValues: event ? fromEvent(event) : EMPTY_FORM,
  });
  const { control, setValue } = form;
  const isSubmitting =
    form.formState.isSubmitting || createSeedEvent.isPending || updateSeedEvent.isPending;

  const sourceMode = useWatch({ control, name: "sourceMode" });
  const eventType = useWatch({ control, name: "eventType" });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const useRegistry = values.sourceMode === "registry";
      const payload = {
        title: values.title,
        summary: values.summary,
        source_id: useRegistry ? values.sourceId || null : null,
        source_url: useRegistry ? null : values.sourceUrl || null,
        source_name: useRegistry ? null : values.sourceName || null,
        event_date: values.eventDate || null,
        event_type: values.eventType,
        tickers: values.tickers.length ? values.tickers : null,
        entities: values.entities.length ? values.entities : null,
        geography: values.geography.length ? values.geography : null,
        asset_class: values.assetClass.length ? values.assetClass : null,
        cooldown_days: values.cooldownDays !== "" ? parseInt(values.cooldownDays, 10) : null,
      };
      if (isEditing) {
        await updateSeedEventAsync({ id: event.id, data: payload });
      } else {
        await createSeedEventAsync(payload);
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save seed event");
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="font-semibold text-xl">
            {isEditing ? "Edit Seed Event" : "Add Seed Event"}
          </h2>
          <button onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}

            <div className="space-y-4">
              <FormField
                control={control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Title <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Fed holds rates as inflation stays above target"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Summary <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={3}
                        placeholder="Brief description of the event and why it's relevant for topic generation..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="eventType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Event Type</FormLabel>
                      <div className="flex gap-2">
                        {(["news", "evergreen"] as SeedEventType[]).map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => field.onChange(t)}
                            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 font-medium text-sm transition-colors ${
                              field.value === t
                                ? t === "news"
                                  ? "border-blue-400 bg-blue-50 text-blue-700"
                                  : "border-green-400 bg-green-50 text-green-700"
                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {t === "news" ? (
                              <Newspaper className="h-3.5 w-3.5" />
                            ) : (
                              <TreePine className="h-3.5 w-3.5" />
                            )}
                            {t === "news" ? "News" : "Evergreen"}
                          </button>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="eventDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Event Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name="sourceMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <div className="mb-2 flex overflow-hidden rounded-lg border border-gray-300">
                      {(["registry", "manual"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => {
                            field.onChange(mode);
                            setValue("sourceId", "");
                            setValue("sourceUrl", "");
                            setValue("sourceName", "");
                          }}
                          className={`flex-1 px-3 py-2 font-medium text-sm transition-colors ${field.value === mode ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        >
                          {mode === "registry" ? "Select from registry" : "Enter manually"}
                        </button>
                      ))}
                    </div>
                    {sourceMode === "registry" ? (
                      <Controller
                        control={control}
                        name="sourceId"
                        render={({ field: sf }) => (
                          <Select
                            value={sf.value ? sf.value : ALL}
                            onValueChange={(v) => sf.onChange(v === ALL ? "" : v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="— Select a source —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={ALL}>— Select a source —</SelectItem>
                              {sources.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <Controller
                          control={control}
                          name="sourceName"
                          render={({ field: sf }) => (
                            <div>
                              <FormLabel className="mb-1 block text-gray-600 text-xs">
                                Source Name
                              </FormLabel>
                              <Input {...sf} placeholder="e.g. Reuters" />
                            </div>
                          )}
                        />
                        <Controller
                          control={control}
                          name="sourceUrl"
                          render={({ field: sf }) => (
                            <div>
                              <FormLabel className="mb-1 block text-gray-600 text-xs">
                                Source URL
                              </FormLabel>
                              <Input type="url" {...sf} placeholder="https://..." />
                            </div>
                          )}
                        />
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="tickers"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tickers</FormLabel>
                      <TagInput
                        values={field.value}
                        onChange={field.onChange}
                        placeholder="AAPL, MSFT… (Enter)"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="entities"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Entities</FormLabel>
                      <TagInput
                        values={field.value}
                        onChange={field.onChange}
                        placeholder="Federal Reserve… (Enter)"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={control}
                name="geography"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Geography</FormLabel>
                    <MultiSelect
                      options={GEOGRAPHY_VALUES}
                      values={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="assetClass"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asset Class</FormLabel>
                    <MultiSelect
                      options={ASSET_CLASS_VALUES}
                      values={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="cooldownDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cooldown days</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        {...field}
                        placeholder={
                          eventType === "news"
                            ? "14 (default for news)"
                            : "None (default for evergreen)"
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      How long before this event can be used again. Leave blank to use the default.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4" />
                )}
                {isSubmitting ? "Saving…" : isEditing ? "Save Changes" : "Submit"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

// ── Compact tag chips ─────────────────────────────────────────────────────────

function CompactTags({ values }: { values: string[] | null }) {
  if (!values?.length) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {values.map((v) => (
        <span key={v} className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600 text-xs">
          {v}
        </span>
      ))}
    </div>
  );
}

function CooldownCell({
  lastUsedAt,
  cooldownDays,
}: {
  lastUsedAt: string | null;
  cooldownDays: number | null;
}) {
  if (!lastUsedAt) return <span className="text-gray-400 text-sm">Never</span>;
  const usedDate = new Date(lastUsedAt);
  const daysSince = Math.floor((Date.now() - usedDate.getTime()) / 86_400_000);
  const inCooldown = cooldownDays != null && daysSince < cooldownDays;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-gray-600 text-sm">{usedDate.toLocaleDateString()}</span>
      {inCooldown && cooldownDays != null && (
        <span className="font-medium text-amber-600 text-xs">
          {cooldownDays - daysSince}d cooldown left
        </span>
      )}
    </div>
  );
}

// ── Generation job poller ──────────────────────────────────────────────────────

interface ActiveJob {
  eventId: string;
  jobId: string;
}

type GenerationToast = {
  type: "success" | "error";
  message: string;
  jobStarted: boolean;
};

/**
 * Drives the polling + terminal side-effects for one in-flight generation job.
 * Renders nothing — `useGenerationJob` polls (refetchInterval, stops on
 * completed/failed) and an effect fires the completion/error callback exactly
 * once when the job query's data reaches a terminal status.
 */
function GenerationJobPoller({
  job,
  onTerminal,
}: {
  job: ActiveJob;
  onTerminal: (eventId: string, result: { ok: true } | { ok: false; message: string }) => void;
}) {
  const { data } = useGenerationJob(job.jobId, { poll: true });
  const status = data?.status;

  useEffect(() => {
    if (status === "completed") {
      onTerminal(job.eventId, { ok: true });
    } else if (status === "failed") {
      onTerminal(job.eventId, {
        ok: false,
        message: data?.error_message || "Generation failed",
      });
    }
  }, [status, data?.error_message, job.eventId, onTerminal]);

  return null;
}

// ── Page ──────────────────────────────────────────────────────────────────────

type FilterValue<T extends string> = "all" | T;

export function SeedEventsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [statusFilter, setStatusFilter] = useState<FilterValue<SeedEventStatus>>("all");
  const [typeFilter, setTypeFilter] = useState<FilterValue<SeedEventType>>("all");

  const [modalEvent, setModalEvent] = useState<SeedEvent | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [generateModalEvent, setGenerateModalEvent] = useState<SeedEvent | null>(null);
  // In-flight generation jobs, keyed by the seed event that started them. Each
  // mounts a <GenerationJobPoller> that polls and fires terminal side-effects.
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [generationToast, setGenerationToast] = useState<GenerationToast | null>(null);

  const sort = sorting[0];
  const listParams = {
    page,
    page_size: PAGE_SIZE,
    status: statusFilter === "all" ? undefined : statusFilter,
    event_type: typeFilter === "all" ? undefined : typeFilter,
    q: search || undefined,
    sort_by: sort?.id,
    sort_order: sort ? ((sort.desc ? "desc" : "asc") as "asc" | "desc") : undefined,
  };
  const seedEventsQuery = useSeedEvents(listParams);
  const data = seedEventsQuery.data ?? null;
  const isLoading = seedEventsQuery.isLoading;
  const error = seedEventsQuery.error
    ? seedEventsQuery.error instanceof Error
      ? seedEventsQuery.error.message
      : "Failed to load seed events"
    : null;
  const { refetch: refetchSeedEvents } = seedEventsQuery;

  const sourcesQuery = useSeedEventSources();
  const sources = sourcesQuery.data ?? [];

  const deleteSeedEvent = useDeleteSeedEvent();
  const updateSeedEvent = useUpdateSeedEvent();
  const generateFromSeedEvent = useGenerateFromSeedEvent();
  const { mutateAsync: deleteSeedEventAsync } = deleteSeedEvent;
  const { mutateAsync: updateSeedEventAsync } = updateSeedEvent;
  const { mutateAsync: generateFromSeedEventAsync } = generateFromSeedEvent;

  const generatingIds = useMemo(() => new Set(activeJobs.map((j) => j.eventId)), [activeJobs]);

  const openCreate = () => {
    setModalEvent(undefined);
    setShowModal(true);
  };
  const openEdit = useCallback((event: SeedEvent) => {
    setModalEvent(event);
    setShowModal(true);
  }, []);
  const closeModal = () => {
    setShowModal(false);
    setModalEvent(undefined);
  };

  const handleReject = useCallback(
    async (id: string) => {
      try {
        await deleteSeedEventAsync(id);
      } catch {
        /* ignore */
      }
    },
    [deleteSeedEventAsync]
  );

  const handleRestore = useCallback(
    async (id: string) => {
      try {
        await updateSeedEventAsync({ id, data: { status: "qualified" } });
      } catch {
        /* ignore */
      }
    },
    [updateSeedEventAsync]
  );

  const handleJobTerminal = useCallback(
    (eventId: string, result: { ok: true } | { ok: false; message: string }) => {
      setActiveJobs((prev) => prev.filter((j) => j.eventId !== eventId));
      if (result.ok) {
        setGenerationToast({
          type: "success",
          message: "Topics generated — view in Review Queue",
          jobStarted: false,
        });
        setTimeout(() => setGenerationToast(null), 8000);
        refetchSeedEvents();
      } else {
        setGenerationToast({ type: "error", message: result.message, jobStarted: false });
        setTimeout(() => setGenerationToast(null), 8000);
      }
    },
    [refetchSeedEvents]
  );

  const handleGenerate = async (eventId: string, count = 1) => {
    setGenerationToast(null);
    try {
      const job = await generateFromSeedEventAsync({ eventId, count });
      setActiveJobs((prev) => [
        ...prev.filter((j) => j.eventId !== eventId),
        { eventId, jobId: job.id },
      ]);
      setGenerationToast({ type: "success", message: "Generation started", jobStarted: true });
      setTimeout(() => setGenerationToast(null), 8000);
    } catch {
      setGenerationToast({
        type: "error",
        message: "Failed to start generation",
        jobStarted: false,
      });
      setTimeout(() => setGenerationToast(null), 6000);
    }
  };

  const columns = useMemo<ColumnDef<SeedEvent>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Event",
        cell: ({ row }) => {
          const event = row.original;
          return (
            <div>
              <div className="font-medium text-gray-900 text-sm">{event.title}</div>
              {event.summary && (
                <div className="mt-0.5 line-clamp-1 text-gray-500 text-xs">{event.summary}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "event_type",
        header: "Type",
        cell: ({ row }) => <EventTypeBadge type={row.original.event_type} />,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge tone={STATUS_TONES[row.original.status]}>
            {STATUS_LABELS[row.original.status]}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "event_date",
        header: "Date",
        cell: ({ row }) =>
          row.original.event_date ? (
            <span className="text-gray-600 text-sm">
              {new Date(row.original.event_date).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
      },
      {
        id: "source",
        header: "Source",
        enableSorting: false,
        cell: ({ row }) => {
          const event = row.original;
          const displaySource = event.source?.name || event.source_name || event.source_url;
          return (
            <span className="block max-w-[160px] truncate text-gray-600 text-sm">
              {displaySource ?? <span className="text-gray-400">—</span>}
            </span>
          );
        },
      },
      {
        id: "geography",
        header: "Geography",
        enableSorting: false,
        cell: ({ row }) => <CompactTags values={row.original.geography} />,
      },
      {
        id: "asset_class",
        header: "Asset Class",
        enableSorting: false,
        cell: ({ row }) => <CompactTags values={row.original.asset_class} />,
      },
      {
        id: "last_used",
        header: "Last Used",
        enableSorting: false,
        cell: ({ row }) => (
          <CooldownCell
            lastUsedAt={row.original.last_used_at}
            cooldownDays={row.original.cooldown_days}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const event = row.original;
          const isGenerating = generatingIds.has(event.id);
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-lg p-1.5 transition-colors hover:bg-gray-100">
                  <MoreVertical className="h-4 w-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEdit(event)}>Edit</DropdownMenuItem>
                <DropdownMenuItem disabled>Evaluate</DropdownMenuItem>
                {event.status === "qualified" && (
                  <DropdownMenuItem
                    onClick={() => setGenerateModalEvent(event)}
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Generating…
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate Topics
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {event.status === "rejected" ? (
                  <DropdownMenuItem onClick={() => handleRestore(event.id)}>
                    Restore to Qualified
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => handleReject(event.id)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Reject
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [generatingIds, openEdit, handleReject, handleRestore]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setPage(1); // re-sort from the first page
      return next;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {/* Generation toast */}
        {generationToast && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-xl px-4 py-3 font-medium text-sm ${
              generationToast.type === "success"
                ? "border border-green-200 bg-green-50 text-green-800"
                : "border border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {generationToast.type === "success" ? (
              <CheckCircle className="h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0" />
            )}
            {generationToast.message}
            {generationToast.type === "success" && generationToast.jobStarted && (
              <a
                href="/admin/topics?view=generate&status=all"
                className="ml-2 text-green-700 underline hover:text-green-900"
              >
                View Generation Jobs →
              </a>
            )}
            {generationToast.type === "success" && !generationToast.jobStarted && (
              <a
                href="/admin/topics?view=queue"
                className="ml-2 text-green-700 underline hover:text-green-900"
              >
                Go to Review Queue →
              </a>
            )}
          </div>
        )}

        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetchSeedEvents()}
          emptyMessage="No seed events found"
          getRowId={(e) => e.id}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search seed events…"
              />
              <div className="flex items-center gap-2">
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v as FilterValue<SeedEventStatus>);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {(Object.keys(STATUS_LABELS) as SeedEventStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={typeFilter}
                  onValueChange={(v) => {
                    setTypeFilter(v as FilterValue<SeedEventType>);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="evergreen">Evergreen</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={openCreate}
                  className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4" />
                  Add Seed Event
                </button>
              </div>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>

      {showModal && <SeedEventModal event={modalEvent} sources={sources} onClose={closeModal} />}

      {generateModalEvent && (
        <GenerateModal
          event={generateModalEvent}
          onClose={() => setGenerateModalEvent(null)}
          onStarted={(eventId, count) => handleGenerate(eventId, count)}
        />
      )}

      {activeJobs.map((job) => (
        <GenerationJobPoller key={job.jobId} job={job} onTerminal={handleJobTerminal} />
      ))}
    </AdminLayout>
  );
}
